"use client";

import { motion } from "framer-motion";
import { Circle } from "lucide-react";
import type { ReactElement, ReactNode } from "react";
import { cn } from "@/lib/utils";

type ElegantShapeProps = {
    className?: string;
    delay?: number;
    width?: number;
    height?: number;
    rotate?: number;
    gradient?: string;
};

function ElegantShape({
    className,
    delay = 0,
    width = 400,
    height = 100,
    rotate = 0,
    gradient = "from-inverse-highlight/18",
}: ElegantShapeProps): ReactElement {
    return (
        <motion.div
            initial={{
                opacity: 0,
                y: -150,
                rotate: rotate - 15,
            }}
            animate={{
                opacity: 1,
                y: 0,
                rotate: rotate,
            }}
            transition={{
                duration: 2.4,
                delay,
                ease: [0.23, 0.86, 0.39, 0.96],
                opacity: { duration: 1.2 },
            }}
            className={cn("absolute", className)}
        >
            <motion.div
                animate={{
                    y: [0, 15, 0],
                }}
                transition={{
                    duration: 18,
                    repeat: Infinity,
                    ease: "easeInOut",
                }}
                style={{
                    width,
                    height,
                }}
                className="relative"
            >
                <div
                    className={cn(
                        "absolute inset-0 rounded-full",
                        "bg-gradient-to-r to-transparent",
                        gradient,
                        "backdrop-blur-[2px] border-2 border-inverse-border/45",
                        "shadow-inverse-soft",
                        "after:absolute after:inset-0 after:rounded-full",
                        "after:bg-[radial-gradient(circle_at_50%_50%,hsl(var(--inverse-highlight)/0.2),transparent_70%)]"
                    )}
                />
            </motion.div>
        </motion.div>
    );
}

type ShapeLandingBackgroundProps = {
    as?: "div" | "main" | "section";
    className?: string;
    contentClassName?: string;
    children: ReactNode;
    backgroundSlot?: ReactNode;
    showDecorations?: boolean;
    minHeightClassName?: string;
};

function ShapeLandingBackground({
    as: Component = "div",
    className,
    contentClassName,
    children,
    backgroundSlot,
    showDecorations = true,
    minHeightClassName = "min-h-screen",
}: ShapeLandingBackgroundProps): ReactElement {
    return (
        <Component
            className={cn("relative w-full overflow-hidden bg-inverse", minHeightClassName, className)}
        >
            {backgroundSlot ? (
                <div className="pointer-events-none absolute inset-0" aria-hidden="true">
                    {backgroundSlot}
                </div>
            ) : (
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-indigo-500/[0.05] via-transparent to-rose-500/[0.05] blur-3xl" />
            )}

            {showDecorations && (
                <div className="pointer-events-none absolute inset-0 overflow-hidden">
                    <ElegantShape
                        delay={0.3}
                        width={600}
                        height={140}
                        rotate={12}
                        gradient="from-indigo-500/[0.15]"
                        className="left-[-10%] md:left-[-5%] top-[15%] md:top-[20%]"
                    />

                    <ElegantShape
                        delay={0.5}
                        width={500}
                        height={120}
                        rotate={-15}
                        gradient="from-rose-500/[0.15]"
                        className="right-[-5%] md:right-[0%] top-[70%] md:top-[75%]"
                    />

                    <ElegantShape
                        delay={0.4}
                        width={300}
                        height={80}
                        rotate={-8}
                        gradient="from-violet-500/[0.15]"
                        className="left-[5%] md:left-[10%] bottom-[5%] md:bottom-[10%]"
                    />

                    <ElegantShape
                        delay={0.6}
                        width={200}
                        height={60}
                        rotate={20}
                        gradient="from-amber-500/[0.15]"
                        className="right-[15%] md:right-[20%] top-[10%] md:top-[15%]"
                    />

                    <ElegantShape
                        delay={0.7}
                        width={150}
                        height={40}
                        rotate={-25}
                        gradient="from-cyan-500/[0.15]"
                        className="left-[20%] md:left-[25%] top-[5%] md:top-[10%]"
                    />

                    <ElegantShape
                        delay={0.45}
                        width={260}
                        height={90}
                        rotate={4}
                        gradient="from-emerald-400/[0.14]"
                        className="left-1/2 top-[45%] transform -translate-x-1/2 md:top-[42%]"
                    />

                    <ElegantShape
                        delay={0.52}
                        width={180}
                        height={60}
                        rotate={-10}
                        gradient="from-inverse-foreground/15"
                        className="left-1/2 top-[62%] transform -translate-x-1/2 md:top-[58%]"
                    />
                </div>
            )}

            <div className={cn("relative z-10", contentClassName)}>
                {children}
            </div>

            <div className="absolute inset-0 bg-gradient-to-t from-inverse via-transparent to-inverse/80 pointer-events-none" />
        </Component>
    );
}

type HeroGeometricProps = {
    badge?: string;
    title1?: string;
    title2?: string;
    children?: ReactNode;
};

function HeroGeometric({
    badge = "Design Collective",
    title1 = "Elevate Your Digital Vision",
    title2 = "Crafting Exceptional Websites",
    children,
}: HeroGeometricProps): ReactElement {
    const fadeUpVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: (i: number) => ({
            opacity: 1,
            y: 0,
            transition: {
                duration: 1,
                delay: 0.5 + i * 0.2,
                ease: [0.25, 0.4, 0.25, 1],
            },
        }),
    };

    const content = children ?? (
        <>
                            <motion.div
                                custom={0}
                                variants={fadeUpVariants}
                                initial="hidden"
                                animate="visible"
                                className="mb-8 inline-flex items-center gap-2 rounded-full border border-inverse-border/45 bg-inverse-surface/40 px-3 py-1 tracking-wide text-inverse-muted-foreground md:mb-12"
                            >
                                <Circle className="h-2 w-2 fill-rose-500/80" />
                                <span className="text-sm">{badge}</span>
                            </motion.div>

                            <motion.div
                                custom={1}
                                variants={fadeUpVariants}
                                initial="hidden"
                                animate="visible"
                            >
                                <h1 className="mb-6 text-4xl font-bold tracking-tight text-inverse-foreground sm:text-6xl md:mb-8 md:text-8xl">
                                    <span className="bg-gradient-to-b from-inverse-foreground to-inverse-foreground/85 bg-clip-text text-transparent">
                                        {title1}
                                    </span>
                                    <br />
                                    <span className="bg-gradient-to-r from-inverse-highlight via-emerald-400/80 to-rose-400/80 bg-clip-text text-transparent">
                                        {title2}
                                    </span>
                                </h1>
                            </motion.div>

                            <motion.div
                                custom={2}
                                variants={fadeUpVariants}
                                initial="hidden"
                                animate="visible"
                            >
                                <p className="mx-auto mb-8 max-w-xl px-4 text-base font-light leading-relaxed tracking-wide text-inverse-muted-foreground/70 sm:text-lg md:text-xl">
                                    Crafting exceptional digital experiences through innovative design and cutting-edge technology.
                                </p>
                            </motion.div>
        </>
    );

    return (
        <ShapeLandingBackground className="flex min-h-screen items-center justify-center text-inverse-foreground">
            <div className="container mx-auto px-4 md:px-6">
                <div className="mx-auto max-w-3xl text-center">
                    {content}
                </div>
            </div>
        </ShapeLandingBackground>
    );
}

export { ElegantShape, HeroGeometric, ShapeLandingBackground }
